import React, { useEffect, useRef, useState } from 'react';
import { HeroCursor } from './HeroCursor';
const HEADLINE = 'I help founders go from concept to capital.';
interface CursorPosition {
  x: number;
  y: number;
}
export function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isFinePointer, setIsFinePointer] = useState(false);
  const [isCursorVisible, setIsCursorVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isTitleHovered, setIsTitleHovered] = useState(false);
  const [titleGlowPosition, setTitleGlowPosition] = useState<CursorPosition>({
    x: 0,
    y: 0
  });
  const [cursorPosition, setCursorPosition] = useState<CursorPosition>({
    x: 0,
    y: 0
  });
  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const syncPointerCapability = () => setIsFinePointer(mediaQuery.matches);
    syncPointerCapability();
    mediaQuery.addEventListener('change', syncPointerCapability);
    return () => mediaQuery.removeEventListener('change', syncPointerCapability);
  }, []);
  useEffect(() => {
    const endDrag = () => setIsDragging(false);
    window.addEventListener('mouseup', endDrag);
    return () => window.removeEventListener('mouseup', endDrag);
  }, []);
  const updateCursorPosition = (event: React.MouseEvent<HTMLElement>) => {
    const bounds = sectionRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setCursorPosition({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top
    });
  };
  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (!isFinePointer) return;
    updateCursorPosition(event);
    setIsCursorVisible(true);
  };
  const handleMouseMove = (event: React.MouseEvent<HTMLElement>) => {
    if (!isFinePointer || isDragging) return;
    updateCursorPosition(event);
  };
  const handleMouseLeave = () => {
    setIsCursorVisible(false);
    setIsDragging(false);
    setIsTitleHovered(false);
  };
  const updateTitleGlow = (event: React.MouseEvent<HTMLHeadingElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    setTitleGlowPosition({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top
    });
  };
  const handleTitleMouseEnter = (event: React.MouseEvent<HTMLHeadingElement>) => {
    if (!isFinePointer) return;
    updateTitleGlow(event);
    setIsTitleHovered(true);
  };
  const handleTitleMouseLeave = () => setIsTitleHovered(false);
  const handleMouseDown = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    const isHeroDraggable = target.closest('[data-hero-draggable="true"]') !== null;
    if (isFinePointer && event.button === 0 && isHeroDraggable) {
      setIsDragging(true);
      setIsCursorVisible(false);
    }
  };
  const showCustomCursor = isFinePointer && isCursorVisible && !isDragging;
  return <section ref={sectionRef} className="relative min-h-[600px] sm:min-h-[680px] md:min-h-[720px] w-full flex flex-col overflow-hidden pt-28 pb-12" style={{
    background: 'var(--bg)',
    cursor: isDragging ? 'grabbing' : showCustomCursor ? 'none' : 'auto'
  }} onMouseEnter={handleMouseEnter} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onMouseDown={handleMouseDown} data-id="element-80" data-mp="vige19z3c5e7">
      {/* Main content */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-6 md:px-12 text-center z-20" data-id="element-81" data-mp="3l3gvlothbinu">
        <h1 className="relative font-aspekta text-[clamp(2.5rem,5vw,4.75rem)] leading-[1.05] tracking-tight mb-8 animate-fade-up delay-100" style={{
        color: 'var(--fg)',
        maxWidth: '18ch'
      }} onMouseEnter={handleTitleMouseEnter} onMouseMove={updateTitleGlow} onMouseLeave={handleTitleMouseLeave} data-id="element-82" data-mp="2odhcx1jm73rj">
          {HEADLINE}
          {isTitleHovered && showCustomCursor && <span className="pointer-events-none absolute inset-0" style={{
          color: 'transparent',
          backgroundImage: `radial-gradient(110px circle at ${titleGlowPosition.x}px ${titleGlowPosition.y}px, rgba(217, 119, 87, 0.3) 0%, rgba(217, 119, 87, 0.12) 34%, rgba(217, 119, 87, 0) 72%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text'
        }} aria-hidden="true" data-id="element-83" data-mp="29otkh452fa5t">
              {HEADLINE}
            </span>}
        </h1>

        <p className="text-base sm:text-lg md:text-xl leading-relaxed mb-10 md:mb-12 animate-fade-up delay-200" style={{
        color: 'var(--muted)',
        maxWidth: '50ch',
        fontWeight: 400
      }} data-id="element-84" data-mp="tzlnuzpxjtat">
          I'm Stella Pangilinan. I've led product for startups across 4
          countries that have gone on to raise $4M+, and redesigned systems
          that run $30M+ in orders.
        </p>

        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 animate-fade-up delay-300" data-id="element-85" data-mp="35qplbkwwe64b">
          <a href="#work" className="font-aspekta text-base font-normal px-8 py-4 rounded-full transition-colors duration-200" style={{
          background: 'var(--accent)',
          color: '#FFFFFF'
        }} onMouseEnter={e => e.currentTarget.style.background = '#D95D3E'} onMouseLeave={e => e.currentTarget.style.background = 'var(--accent)'} data-id="element-86" data-mp="2vn24t7g1sc8m">
            See my work
          </a>
          <a href="#about" className="font-aspekta text-base font-normal px-8 py-4 rounded-full transition-colors duration-200" style={{
          color: 'var(--fg)',
          border: '1px solid var(--border)'
        }} onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--fg)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'} data-id="element-87" data-mp="22w0kyt1etsir">
            About me
          </a>
        </div>
      </div>

      {/* Day-mode ambient layer */}
      <div className="hero-ambient absolute bottom-0 left-0 right-0 h-[40vh] z-0 pointer-events-none" style={{
      background: 'linear-gradient(to top, rgba(222, 214, 199, 0.55) 0%, rgba(247,245,238,0) 100%)'
    }} data-id="element-88" data-mp="3vs385qryn9vr" />

      {showCustomCursor && <HeroCursor x={cursorPosition.x} y={cursorPosition.y} data-id="element-89" data-mp="nbrkhk9fhzs0" />}
    </section>;
}